// Created: 2026-08-14
// Updated: 2026-08-14
//
// The single source of key truth for the USER RELATIONS section (react-query-user-relations, Req 1).
//
// Inherited conventions (slices 1–6): keys are hierarchical arrays of JSON-serializable primitives,
// element 0 names the resource, PERSONAL data is namespaced under a leading 'me', and a parameter earns
// a place in a key only when it selects a DIFFERENT resource rather than filtering one.
//
// ─── THERE IS EXACTLY ONE KEY, AND NO PUBLIC KEY ──────────────────────────────────────────────────
//
// IDENTITY-VARYING AND 401-GATED. `services/lambda-secure/src/routes/get-user-relations.ts` resolves the
// caller with `getIdentityId(event)` and returns 401 without one, then reads EVERY field it answers with
// off `PK = user#{identity_id}` in a single pass — following, followers, favorites, the notification
// rows, the `notification_unread_counter` record, `settings`, and the `cognito:groups`-derived moderator
// flags. The response is scoped to exactly one identity, so there is no identity-invariant half to share:
// **no public key exists here and nothing in this section is prerenderable or dehydratable into
// CDN-cached HTML.** That is the same structural fact slice 6 recorded for notifications, and it is why
// the one key below is `'me'`-namespaced and its query is always gated on a token.
//
// ─── THE ARGUMENT AGAINST A PER-FIELD KEY ─────────────────────────────────────────────────────────
//
// The temptation is `['me','relations','favorites']`, `['me','relations','following']`,
// `['me','relations','isModerator']` — one key per member, each invalidated independently. It is wrong
// twice over:
//
//   1. **N requests for one round-trip.** There is ONE endpoint. The handler assembles every field in a
//      single invocation; splitting the key cannot split the fetch, so each extra key either re-requests
//      the whole payload or invents a fetcher that does not exist.
//   2. **It would give `isModerator` a cache entry of its own** — and `isModerator` is an authorization
//      signal (`ModeratorRoute.tsx:43` decides route access on it), not a display value. A separate entry
//      is a separate thing to purge at sign-out, and it is precisely the entry D1 (design.md § 3) exists
//      to guarantee cannot outlive a session. One key, one purge site, one thing that can leak, closed.
//
// So: no prefix key (there is nothing beneath the one key to reach), no per-field key, and no
// `staleTime` constant — this section takes the client-wide baseline and `queries.ts` carries the
// argument for why (design.md § 2).

export const userRelationsKeys = {
  /**
   * PERSONAL — the caller's entire relations payload in ONE entry, stored verbatim as the handler
   * returned it (Req 1.8). Every consumer-facing value — `isFavorited`, `isFollowing`, `isModerator`,
   * `unpublishedProjects` — is DERIVED from this one entry at render time by
   * `src/context/UserRelationsContext.tsx`, which is what makes it impossible for two consumers to read
   * two different answers out of two copies of the same truth.
   *
   * Both writes in `mutations.ts` `setQueryData` against this key and neither invalidates it: the
   * mutation wrote the new truth itself.
   */
  all: () => ['me', 'relations'] as const,
} as const

/** The union of every key this factory emits — used by the key tests. */
export type UserRelationsKey = ReturnType<typeof userRelationsKeys.all>
